import { MultiOneShotResolver, OneShotResolver } from "./OneShotResolver";
import { ModelPath } from "../modelPath/ModelPath";
import { modelPool } from "../ModelPool";
import { modelUIDRegistry } from "../modelUIDRegistry/ModelUIDRegistry";
import { NO_MESSAGING } from "../types";
import { determineModelNameForPrinting } from "../modelPoolUtils";
/**
 * As we receive models from peers or the server, their parent or room might not be created, thus we need to collect
 * all the necessary information until we have everything to create a model
 */
export class ModelMessageResolver {
    constructor() {
        this.unresolvedModelDescriptions = new Set();
        this.unresolvedModelUIDs = new Set();
        this.onRoomResolver = new MultiOneShotResolver();
        this.onParentResolver = new MultiOneShotResolver();
        this.onModelResolvedResolver = new MultiOneShotResolver();
        this.onAllModelsResolvedResolver = new OneShotResolver();
    }
    /**
     * Create a model with the specified parameters. If the required parent hasn't been created yet, then the model
     * creation will be delayed until the parent has been created.
     */
    createModel(name, uid, fullPath, isRoom, isStartRoom, metaData) {
        modelUIDRegistry.addEntry(uid, new ModelPath(fullPath));
        // Create room, parent and children if possible
        const room = this.retrieveRoom(uid);
        const parent = this.retrieveParent(uid);
        const children = this.retrieveChildren(uid);
        const modelDescription = {
            uid,
            name,
            fullPath,
            parent,
            room,
            children,
            isRoom,
            isStartRoom,
            metaData
        };
        // A room needs a parent but not necessarily a parent room
        let hasRoom = (isRoom) ? true : room !== undefined;
        let hasParent = parent !== undefined;
        // If we have everything that we need, then we can construct the model directly
        if (hasRoom && hasParent) {
            this.constructModelFromModelDescription(modelDescription);
            const modelName = determineModelNameForPrinting(modelDescription.uid);
            console.debug(`[model] Constructed model "${modelName}" immediately`);
            return;
        }
        // Some information are still missing, so we have to delay the model creation until we have all
        // necessary information. In the meantime we save all information in a partial model description
        this.unresolvedModelDescriptions.add(modelDescription);
        this.unresolvedModelUIDs.add(modelDescription.uid);
        const modelPath = new ModelPath(fullPath);
        const roomPath = modelPath.getRoom();
        const parentPath = modelPath.getParent();
        // We need a parent and a room
        const isResolved = () => {
            return hasParent && hasRoom;
        };
        // We need to wait for the room
        const requirements = [];
        if (!hasRoom && roomPath !== undefined) {
            requirements.push(roomPath.basename());
            this.onRoomResolver.subscribe(roomPath.toString(), (room) => {
                modelDescription.room = room;
                hasRoom = true;
                if (isResolved()) {
                    this.constructModelFromModelDescription(modelDescription);
                    this.unresolvedModelDescriptions.delete(modelDescription);
                    this.unresolvedModelUIDs.delete(modelDescription.uid);
                }
            });
        }
        // We need to wait for the parent
        if (!hasParent && parentPath !== undefined) {
            requirements.push(parentPath.basename());
            this.onParentResolver.subscribe(parentPath.toString(), (parent) => {
                modelDescription.parent = parent;
                hasParent = true;
                if (isResolved()) {
                    this.constructModelFromModelDescription(modelDescription);
                    this.unresolvedModelDescriptions.delete(modelDescription);
                    this.unresolvedModelUIDs.delete(modelDescription.uid);
                }
            });
        }
        console.debug(`[model] Enqueued model with name "${modelDescription.name}" and path "${fullPath}" waiting for requirements "${requirements.join(', ')}" (${this.unresolvedModelUIDs.size} waiting)`);
    }
    /**
     * Returns true if there is still a model with the specified uid that still waits for either room or parent before
     * it can be created.
     */
    hasUnresolvedModel(uid) {
        return this.unresolvedModelUIDs.has(uid);
    }
    hasUnresolvedModels() {
        return this.unresolvedModelUIDs.size > 0;
    }
    /// Private helper methods ///
    constructModelFromModelDescription(description) {
        const uid = description.uid;
        const name = description.name;
        const fullPath = description.fullPath;
        const room = description.room;
        const parent = description.parent;
        const children = description.children;
        const isRoom = description.isRoom;
        const isStartRoom = description.isStartRoom;
        const metaData = description.metaData;
        const modelType = (isRoom) ? "room" : "model";
        if (parent === undefined) {
            console.error(`[model] Cannot create ${modelType} ${name} because no parent was provided`);
            return;
        }
        if (!isRoom && room === undefined) {
            console.error(`[model] Cannot create ${modelType} ${name} because no room was provided`);
            return;
        }
        const model = (isRoom)
            ? modelPool.createRoomModel(name, parent, Object.assign({ uid, fullPath, metaData, isStartRoom }, NO_MESSAGING))
            : modelPool.createModel(name, parent, Object.assign({ uid, fullPath, metaData }, NO_MESSAGING));
        if (model === undefined) {
            console.error(`[model] Couldn't create ${modelType} ${name} from model description`);
            return;
        }
        model.addChildren(children, Object.assign({ metaData }, NO_MESSAGING));
        // Inform other unresolved model descriptions of this model
        this.onParentResolver.emit(fullPath, model);
        if (isRoom)
            this.onRoomResolver.emit(fullPath, model);
        this.onModelResolvedResolver.emit(uid, model);
        // Inform others that we have loaded all models
        if (!this.hasUnresolvedModels())
            this.onAllModelsResolvedResolver.emit();
    }
    retrieveRoom(uid) {
        const roomPath = modelUIDRegistry.getRoom(uid);
        if (roomPath === undefined) {
            return;
        }
        const roomUID = modelUIDRegistry.getUIDFromPath(roomPath);
        return (roomUID !== undefined) ? modelPool.getModel(roomUID) : undefined;
    }
    retrieveParent(uid) {
        const parentPath = modelUIDRegistry.getParent(uid);
        if (parentPath === undefined) {
            return undefined;
        }
        const parentUID = modelUIDRegistry.getUIDFromPath(parentPath);
        return (parentUID !== undefined) ? modelPool.getModel(parentUID) : undefined;
    }
    retrieveChildren(uid) {
        const childUIDs = modelUIDRegistry.getChildren(uid);
        const children = [];
        for (const childUID of childUIDs) {
            const child = modelPool.getModel(childUID);
            if (child === undefined) {
                continue;
            }
            children.push(child);
        }
        return children;
    }
}
